import { useCallback, useEffect, useRef } from 'react';
import { DocumentViewerService } from '@/services/documentViewerService';
import {
  selectDocumentTab,
  usePreviewPanelStore,
  type PreviewRequestId,
  type PreviewTabKey,
} from '@/stores/previewPanelStore';

let nextOcrRequestSequence = 0;

function createOcrRequestId(): PreviewRequestId {
  nextOcrRequestSequence += 1;
  return `document-preview-ocr-${nextOcrRequestSequence}`;
}

export interface DocumentPreviewOcrController {
  loadText: () => Promise<void>;
  extractText: () => Promise<void>;
}

const OCR_POLL_INTERVAL_MS = 2_000;
const OCR_MAX_POLLS = 45;

interface PendingOcrRequest {
  tabKey: PreviewTabKey;
  requestId: PreviewRequestId;
  timerId: number | null;
  resolveTimer: (() => void) | null;
}

function isCurrentOcrRequest(tabKey: PreviewTabKey, requestId: PreviewRequestId): boolean {
  return selectDocumentTab(usePreviewPanelStore.getState(), tabKey)?.ocr.requestId === requestId;
}

/** Owns OCR requests for one preview tab and commits results through its keyed store contract. */
export function useDocumentPreviewOcr(tabKey: PreviewTabKey): DocumentPreviewOcrController {
  const tabExists = usePreviewPanelStore((state) => Boolean(selectDocumentTab(state, tabKey)));
  const pendingRequestRef = useRef<PendingOcrRequest | null>(null);

  const cancelPendingRequest = useCallback(() => {
    const pending = pendingRequestRef.current;
    if (!pending) return;
    pendingRequestRef.current = null;
    if (pending.timerId !== null) window.clearTimeout(pending.timerId);
    pending.resolveTimer?.();
    usePreviewPanelStore.getState().cancelOcr(pending.tabKey, pending.requestId);
  }, []);

  const beginRequest = useCallback((requestTabKey: PreviewTabKey, requestId: PreviewRequestId) => {
    cancelPendingRequest();
    const pending: PendingOcrRequest = {
      tabKey: requestTabKey,
      requestId,
      timerId: null,
      resolveTimer: null,
    };
    pendingRequestRef.current = pending;
    return pending;
  }, [cancelPendingRequest]);

  const finishRequest = useCallback((pending: PendingOcrRequest) => {
    if (pendingRequestRef.current === pending) pendingRequestRef.current = null;
  }, []);

  const waitForNextPoll = useCallback((pending: PendingOcrRequest): Promise<void> => (
    new Promise((resolve) => {
      const complete = () => {
        pending.timerId = null;
        pending.resolveTimer = null;
        resolve();
      };
      pending.resolveTimer = complete;
      pending.timerId = window.setTimeout(complete, OCR_POLL_INTERVAL_MS);
    })
  ), []);

  useEffect(() => () => cancelPendingRequest(), [tabKey, tabExists, cancelPendingRequest]);

  const loadText = useCallback(async () => {
    // `selectDocumentTab` et non un accès direct : un onglet jurisprudence n'a
    // ni documentId ni OCR, et doit sortir ici plutôt que par un cast.
    const tab = selectDocumentTab(usePreviewPanelStore.getState(), tabKey);
    if (!tab?.documentId) return;

    const requestId = createOcrRequestId();
    const pending = beginRequest(tabKey, requestId);
    usePreviewPanelStore.getState().startOcr(tabKey, requestId);
    const result = await DocumentViewerService.getOcrText(tab.documentId);
    if (pendingRequestRef.current !== pending) return;
    finishRequest(pending);
    usePreviewPanelStore.getState().resolveOcr(
      tabKey,
      requestId,
      result?.content ?? null,
      result?.source ?? null,
    );
  }, [beginRequest, finishRequest, tabKey]);

  const extractText = useCallback(async () => {
    // `selectDocumentTab` et non un accès direct : un onglet jurisprudence n'a
    // ni documentId ni OCR, et doit sortir ici plutôt que par un cast.
    const tab = selectDocumentTab(usePreviewPanelStore.getState(), tabKey);
    if (!tab?.documentId) return;

    const requestId = createOcrRequestId();
    const pending = beginRequest(tabKey, requestId);
    usePreviewPanelStore.getState().startOcr(tabKey, requestId, 'extracting');
    const extractionStarted = await DocumentViewerService.triggerOcrExtract(tab.documentId);
    if (pendingRequestRef.current !== pending || !isCurrentOcrRequest(tabKey, requestId)) return;

    if (!extractionStarted) {
      usePreviewPanelStore.getState().rejectOcr(
        tabKey,
        requestId,
        "Impossible de lancer l'extraction du texte.",
      );
      finishRequest(pending);
      return;
    }

    for (let poll = 0; poll < OCR_MAX_POLLS; poll += 1) {
      await waitForNextPoll(pending);
      if (pendingRequestRef.current !== pending || !isCurrentOcrRequest(tabKey, requestId)) return;

      const result = await DocumentViewerService.getOcrText(tab.documentId);
      if (pendingRequestRef.current !== pending || !isCurrentOcrRequest(tabKey, requestId)) return;
      if (result?.content) {
        finishRequest(pending);
        usePreviewPanelStore.getState().resolveOcr(
          tabKey,
          requestId,
          result.content,
          result.source,
        );
        return;
      }
    }

    finishRequest(pending);
    usePreviewPanelStore.getState().rejectOcr(
      tabKey,
      requestId,
      "L'extraction prend plus de temps que prévu. Réessayez dans un instant.",
    );
  }, [beginRequest, finishRequest, tabKey, waitForNextPoll]);

  return { loadText, extractText };
}
